import zIndex from '@mui/material/styles/zIndex';
import React from 'react';
import Select from 'react-select';
import { colours } from 'utils/constants';
import { getNestedValue } from 'utils/helpers';

type OptionsProps = {
  value: string;
  label: string;
};

type SelectInputProps = {
  name: string;
  label: string;
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
  options?: OptionsProps[];
  wrapperClass?: string;
  Controller: any;
  control: any;
  errors: any;
  defaultValue?: any;
};

export default function SelectInput({
  name,
  label,
  placeholder,
  disabled = false,
  loading = false,
  options,
  wrapperClass,
  Controller,
  control,
  errors,
  defaultValue,
  ...rest
}: SelectInputProps) {
  const error = getNestedValue(errors, name);
  const customStyles = {
    // This style helps ensure the menu is correctly positioned when using portals
    menuPortal: (base: any) => ({ ...base, zIndex: zIndex.modal }),

    // Customizing the dropdown menu's appearance
    menu: (base: any) => ({
      ...base,
      backgroundColor: colours.selectLightTeal,
      borderColor: colours.selectBorderTeal,
      borderStyle: 'solid',
      marginTop: '0.25rem',
      paddingLeft: '10px',
      paddingRight: '10px',
      width: '100%',
      borderRadius: '0.25rem'
    }),

    // Customizing the appearance of options in the dropdown
    option: (base: any, state: any) => ({
      ...base,
      fontSize: '15px', // Set the desired font size for options
      backgroundColor: state.isSelected
        ? colours.selectIsSelectedTeal
        : state.isFocused
        ? colours.selectHoverTeal // Apply hover color on keyboard focus
        : colours.selectLightTeal,
      color: state.isSelected ? colours.selectIsSelectedText : colours.selectOptionsText,
      borderRadius: '0.25rem',
      ':hover': {
        backgroundColor: colours.selectHoverTeal,
        borderRadius: '0.25rem'
      }
    })
  };

  const menuPortalTarget = document.getElementById('root');

  return (
    <div className={`${wrapperClass} flex flex-col items-start pb-4 text-sm`}>
      {label && (
        <label className="text-neutral9 font-semibold" htmlFor={name}>
          {label}
        </label>
      )}
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        {...rest}
        render={({ field: { onChange, value } }: any) => {
          return (
            <Select
              inputId={name}
              key={`my_unique_select_key__${JSON.stringify(value)}`}
              className="select w-full"
              placeholder={placeholder}
              isDisabled={disabled}
              isLoading={loading}
              isClearable
              isSearchable
              options={options}
              onChange={(opts: any) => onChange(opts ? opts.value : '')}
              closeMenuOnSelect
              value={options?.find((opt) => opt.value === value) || null}
              styles={customStyles}
              menuPortalTarget={menuPortalTarget}
            />
          );
        }}
      />

      {error && (
        <span className="error" role="alert">
          {error.message}
        </span>
      )}
    </div>
  );
}
